import { Link } from "react-router-dom";
import { MODULES, COMPLIANCE_BADGES, CASE_STUDIES } from "@/data/content";
import ModuleCard from "@/components/ModuleCard";
import MarqueeLogos from "@/components/MarqueeLogos";
import RoiCalculator from "@/components/RoiCalculator";
import SEO, { organizationSchema, softwareApplicationSchema } from "@/components/SEO";

export default function Home() {
  return (
    <div data-testid="page-home">
      <SEO
        title="CareSenta — HIPAA-compliant AI operating system for healthcare"
        description="Six AI modules — prior auth autopilot, clinical scribe, refills, care gaps, no-show predictor, denial rebuttal — that run inside your existing EHR. FHIR R4 native. SOC 2 in flight."
        path="/"
        jsonLd={[
          organizationSchema(),
          softwareApplicationSchema({
            name: "CareSenta",
            description: "HIPAA-compliant AI operating system for healthcare — six modules that run inside your existing EHR.",
            url: "https://caresenta.com/",
            features: [
              "Prior Auth Autopilot",
              "Clinical Note Scribe",
              "Rx Refill Concierge",
              "Care Gap Closer",
              "No-Show Predictor",
              "Denial Rebuttal AI",
            ],
            price: "20000",
          }),
        ]}
      />
      {/* HERO */}
      <section className="relative border-b border-[#D6D2C4] overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" aria-hidden />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 fade-in-up">
              <div className="mono-label text-[#8B2B3A]">
                <span className="ticker-dot inline-block mr-3" aria-hidden />
                CareSenta · The OS for modern healthcare
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight mt-6 text-[#0A140F]">
                Run the admin <span className="italic font-normal">and</span> clinical ops of your practice on AI<span className="text-[#8B2B3A]">.</span>
              </h1>
              <p className="mt-7 text-lg sm:text-xl text-[#4A5A52] max-w-2xl leading-relaxed">
                Six HIPAA-compliant AI modules — prior auth, scribe, no-show, refills, care gaps, denials —
                running quietly inside your existing EHR. Zero new screens. Zero change management.
              </p>

              {/* EHR compatibility strip */}
              <div className="mt-7 flex items-center flex-wrap gap-2.5 max-w-3xl" data-testid="home-ehr-strip">
                <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#8A9992] mr-1">Runs alongside:</span>
                {["Epic", "Athena", "Cerner", "NextGen", "eClinicalWorks", "Elation"].map((e) => (
                  <span
                    key={e}
                    className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4A5A52] bg-white/70 border border-[#D6D2C4] rounded-full px-2.5 py-1"
                    data-testid={`home-ehr-${e.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {e}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-[13px] text-[#4A5A52]" data-testid="home-ehr-microcopy">
                Not replacing your EHR. Automating what it can't.{" "}
                <Link to="/works-with-epic" className="font-medium hover:underline" style={{ color: "#0E8B7E" }} data-testid="home-ehr-link">
                  See how →
                </Link>
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link
                  to="/contact"
                  data-testid="home-hero-cta-demo"
                  className="bg-[#8B2B3A] text-[#F8F7F4] px-7 py-4 font-medium hover:bg-[#5A1C26] transition-colors"
                >
                  Request a 20-min demo →
                </Link>
                <Link
                  to="/demo"
                  data-testid="home-hero-cta-try"
                  className="border border-[#0A140F] text-[#0A140F] px-7 py-4 font-medium hover:bg-[#0A140F] hover:text-[#F8F7F4] transition-colors"
                >
                  Try the live AI scribe
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 fade-in-up delay-2">
              <div className="hard-border bg-[#F8F7F4] p-6">
                <div className="mono-label text-[#4A5A52]">System snapshot · last 30 days</div>
                <div className="mt-5 space-y-5">
                  <Fact k="Prior auths auto-filed" v="114,820" />
                  <Fact k="Notes drafted" v="312,047" />
                  <Fact k="Denials recovered" v="$4.8M" />
                  <Fact k="Customer NPS" v="71" />
                </div>
                <div className="mt-6 pt-5 border-t border-[#D6D2C4] mono-label text-[#8A9992]">
                  Across 1,340 providers · 62 organizations
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations marquee */}
      <section className="bg-[#F8F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10">
          <div className="mono-label text-[#4A5A52] shrink-0">Plugs into your EHR →</div>
          <div className="flex-1 min-w-0">
            <MarqueeLogos />
          </div>
        </div>
      </section>

      {/* MODULE GRID */}
      <section className="border-t border-[#D6D2C4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12 lg:mb-16">
            <div className="lg:col-span-8">
              <div className="mono-label text-[#8B2B3A]">Six modules · one runtime</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mt-4 text-[#0A140F]">
                Every admin workflow your staff hates, automated end-to-end.
              </h2>
            </div>
            <div className="lg:col-span-4 text-[#4A5A52] text-[15px] leading-relaxed">
              Each module fires on a single FHIR webhook from your EHR —
              <span className="font-mono text-[13px] bg-[#E8F0EB] px-1.5 py-0.5 ml-1">pa.required</span>,
              <span className="font-mono text-[13px] bg-[#E8F0EB] px-1.5 py-0.5 ml-1">encounter.completed</span>,
              <span className="font-mono text-[13px] bg-[#E8F0EB] px-1.5 py-0.5 ml-1">claim.denied</span> — and returns the work done.
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D6D2C4] border border-[#D6D2C4]">
            {MODULES.map((m) => (
              <ModuleCard key={m.slug} module={m} compact />
            ))}
          </div>
        </div>
      </section>

      {/* ROI */}
      <section className="bg-[#E5E3DB] border-y border-[#D6D2C4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <RoiCalculator />
        </div>
      </section>

      {/* TRUST section dark */}
      <section className="bg-[#0A140F] text-[#F8F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="mono-label text-[#8B2B3A]">Built for PHI</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mt-4">
                HIPAA isn't a checkbox. It's the architecture.
              </h2>
              <p className="mt-6 text-[#8A9992] leading-relaxed">
                CareSenta runs exclusively on HIPAA-eligible AWS services with encryption at rest (AES-256)
                and in transit (TLS 1.3). Every sub-processor signs a BAA. PHI never touches Stripe, logs, or your browser's localStorage.
              </p>
              <Link to="/hipaa" className="inline-block mt-8 border border-[#F8F7F4] text-[#F8F7F4] px-6 py-3 hover:bg-[#F8F7F4] hover:text-[#0A140F] transition-colors" data-testid="home-trust-cta">
                Read the security architecture →
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#2A3E37] border border-[#2A3E37]">
              {COMPLIANCE_BADGES.map((b) => (
                <div key={b.label} className="bg-[#0A140F] p-6" data-testid={`badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-mono font-bold tracking-[0.22em] text-[#F8F7F4]">{b.label}</div>
                  <div className="mt-2 text-xs text-[#8A9992] leading-relaxed">{b.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDIES PREVIEW */}
      <section className="border-t border-[#D6D2C4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <div className="mono-label text-[#8B2B3A]">Signal, not noise</div>
              <h2 className="font-display text-4xl sm:text-5xl leading-none tracking-tight mt-3 text-[#0A140F]">What customers ship.</h2>
            </div>
            <Link to="/customers" className="hidden md:inline-block font-mono text-xs tracking-[0.2em] uppercase text-[#8B2B3A] hover:text-[#5A1C26]" data-testid="home-case-studies-link">
              All case studies →
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[#D6D2C4] border border-[#D6D2C4]">
            {CASE_STUDIES.map((c, i) => (
              <div key={i} className="bg-[#F8F7F4] p-8 flex flex-col gap-6" data-testid={`case-study-preview-${i}`}>
                <div className="mono-label text-[#4A5A52]">{c.region}</div>
                <div className="font-display text-2xl text-[#0A140F] leading-tight">{c.org}</div>
                <p className="text-[15px] leading-relaxed text-[#4A5A52] flex-1">"{c.quote}"</p>
                <div className="mono-label text-[#0A140F]">— {c.attribution}</div>
                <div className="grid grid-cols-3 gap-3 border-t border-[#D6D2C4] pt-4">
                  {c.metrics.map(([k, v], j) => (
                    <div key={j}>
                      <div className="mono-label text-[#8A9992] leading-none">{k}</div>
                      <div className="mt-1.5 font-mono text-base text-[#0A140F]">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-[#D6D2C4] bg-[#F8F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="mono-label text-[#8B2B3A]">v0.1 · accepting early partners</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mt-4 text-[#0A140F]">
              Replace a week of admin work with a Tuesday.
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link to="/contact" className="bg-[#8B2B3A] text-[#F8F7F4] px-7 py-4 font-medium hover:bg-[#5A1C26] transition-colors" data-testid="home-footer-cta-demo">
              Request demo →
            </Link>
            <Link to="/pricing" className="border border-[#0A140F] text-[#0A140F] px-7 py-4 font-medium hover:bg-[#0A140F] hover:text-[#F8F7F4] transition-colors" data-testid="home-footer-cta-pricing">
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Fact({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div className="mono-label text-[#4A5A52]">{k}</div>
      <div className="font-mono text-xl text-[#0A140F] tabular-nums">{v}</div>
    </div>
  );
}
